import { useEffect, useState, useCallback } from "react";
import { ChangeOrdersPanel } from "@/components/ChangeOrdersPanel";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, VENDOR_META, DOC_META, DELIVERY_ORDER, ASSESSMENT_ORDER, PRESALES_ORDER, NON_APPROVAL_DOC_TYPES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { diffLines } from "diff";
import {
  ArrowLeft, UploadCloud, Server, Trash2, FileText, Cpu, Download,
  Eye, Loader2, Sparkles, FileCode2, Network, Pencil, Save, X, FileArchive,
  Share2, Copy, Check, Link2, ClipboardList, History, Plus, ArrowRightLeft, FileUp,
  Trophy, XCircle, CircleDot, Image as ImageIcon, LayoutTemplate, Bookmark,
  MessageSquarePlus, CheckCircle2, RotateCw, Lock, Unlock, Send, ClipboardCheck, ListChecks, Play, Compass,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";

const GEN_META = {
  hld: { label: "HLD", full: "High-Level Design" },
  lld: { label: "LLD", full: "Low-Level Design" },
  asbuilt: { label: "AS-BUILT", full: "As-Built Doc" },
  cover_letter: { label: "COVER", full: "Cover Letter & Exec Summary" },
  solution_overview: { label: "SOLUTION", full: "Solution Overview & Proposed HLD" },
  proposed_hld: { label: "HLD", full: "Proposed High-Level Design" },
  proposed_lld: { label: "LLD", full: "Proposed Low-Level Design" },
  sow: { label: "SOW", full: "Statement of Work" },
  bom: { label: "BOM", full: "Bill of Materials" },
  assumptions: { label: "ASSUMP.", full: "Assumptions & Exclusions" },
  discovery_appendix: { label: "CHECKLIST", full: "Discovery Checklist (Appendix)" },
  diagram: { label: "DIAGRAM", full: "Network Topology" },
};

function fmtWhen(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString("en-AU", { timeZone: "Australia/Sydney", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZoneName: "short" }); }
  catch { return ""; }
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  const [vendor, setVendor] = useState("fortinet");
  const [devName, setDevName] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [selected, setSelected] = useState({ hld: true, lld: true, asbuilt: true, diagram: true });
  const [generating, setGenerating] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [savingDoc, setSavingDoc] = useState(false);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareToken, setShareToken] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const [viewDoc, setViewDoc] = useState(null);
  const [highlightBusy, setHighlightBusy] = useState(false);
  const [viewContent, setViewContent] = useState("");
  const [loadingDoc, setLoadingDoc] = useState(false);

  const [brief, setBrief] = useState({ sites: "", requirements: "", vendors: "", constraints: "", dependencies: "", timeline: "", notes: "" });
  const [savingBrief, setSavingBrief] = useState(false);
  const [sizing, setSizing] = useState({ users: "", circuit_speed_mbps: "", placement: "edge", ssl_mode: "none", utm_level: "threat_protection", growth_headroom_pct: 30, ha: false, ra_vpn_users: "", s2s_tunnels: "", req_ports: { "1g": "", "10g": "", "25g": "" }, logs_per_second: "", log_gb_per_day: "", retention_days: "", firmware_train: "7.6", firmware_maturity: "mature" });
  const [sizingResult, setSizingResult] = useState(null);
  const [sizingBusy, setSizingBusy] = useState(false);
  const [skuCatalog, setSkuCatalog] = useState([]);
  const [skuState, setSkuState] = useState({});
  const [savingSkus, setSavingSkus] = useState(false);
  const [activity, setActivity] = useState([]);
  const [customSections, setCustomSections] = useState([]);
  const [newSection, setNewSection] = useState("");
  const [uploadingBrief, setUploadingBrief] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertPs, setConvertPs] = useState("");
  const [converting, setConverting] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [applyTpl, setApplyTpl] = useState("");
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [revising, setRevising] = useState(false);
  const [versions, setVersions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [verOpen, setVerOpen] = useState(false);
  const [verForm, setVerForm] = useState({ version: "v1.0", stage: "Draft", note: "" });
  const [savingVer, setSavingVer] = useState(false);
  const [wonOpen, setWonOpen] = useState(false);
  const [wonStep, setWonStep] = useState(1);
  const [wonForm, setWonForm] = useState({ version: "v2.0", stage: "Final Signed", note: "" });
  const [wonBusy, setWonBusy] = useState(false);
  const [dlOpen, setDlOpen] = useState(false);
  const [dlSel, setDlSel] = useState({});
  const [fbBusy, setFbBusy] = useState(false);
  const [basicUsers, setBasicUsers] = useState([]);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffA, setDiffA] = useState("");
  const [diffB, setDiffB] = useState("");
  const [diffDoc, setDiffDoc] = useState("");
  const [diffData, setDiffData] = useState(null);

  const isPresales = project?.project_type === "presales";
  const isAssessment = project?.project_type === "assessment";
  const nonPresalesOrder = isAssessment ? ASSESSMENT_ORDER : DELIVERY_ORDER;
  const customKeys = customSections.map((c) => c.key || c.title);
  const genOrder = isPresales
    ? [...PRESALES_ORDER, ...customSections.map((c) => c.key), "diagram"]
    : [...nonPresalesOrder, "diagram"];
  const docOrder = isPresales ? [...PRESALES_ORDER, ...customSections.map((c) => c.key)] : nonPresalesOrder;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      if (data.questionnaire) setBrief((b) => ({ ...b, ...data.questionnaire }));
      if (data.custom_sections) setCustomSections(data.custom_sections);
      if (data.sizing_inputs && Object.keys(data.sizing_inputs).length) {
        setSizing((s) => ({ ...s, ...Object.fromEntries(Object.entries(data.sizing_inputs).map(([k, v]) => [k, v == null ? "" : v])) }));
      }
      if (data.sizing_result && data.sizing_result.recommended_model !== undefined) setSizingResult(data.sizing_result);
      if (data.project_type === "presales") {
        try {
          const { data: cat } = await api.get(`/sku-catalog`);
          const items = cat.items || [];
          setSkuCatalog(items);
          const bySku = {};
          (data.sku_lines || []).forEach((l) => { bySku[l.sku] = l.quantity; });
          const st = {};
          items.forEach((it) => {
            const has = Object.prototype.hasOwnProperty.call(bySku, it.sku);
            st[it.id] = { checked: has, qty: has ? bySku[it.sku] : (it.default_qty || 0) };
          });
          setSkuState(st);
        } catch { /* ignore */ }
      }
    } catch { toast.error("Failed to load project"); }
    finally { setLoading(false); }
  }, [id]);

  const loadActivity = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}/activity`); setActivity(data); }
    catch { /* ignore */ }
  }, [id]);

  useEffect(() => { load(); loadActivity(); }, [load, loadActivity]);

  useEffect(() => {
    if (!project) return;
    const keys = project.project_type === "presales"
      ? [...PRESALES_ORDER, ...customSections.map((c) => c.key), "diagram"]
      : [...(project.project_type === "assessment" ? ASSESSMENT_ORDER : DELIVERY_ORDER), "diagram"];
    setSelected((prev) => {
      const next = { ...prev };
      keys.forEach((k) => { if (next[k] === undefined) next[k] = true; });
      return next;
    });
  }, [project?.project_type, customSections]);

  const metaFor = (dt) => GEN_META[dt] || DOC_META[dt] || {
    label: "SECTION",
    full: (customSections.find((c) => c.key === dt) || {}).title || dt,
  };

  const saveSections = async (sections) => {
    try {
      const { data } = await api.put(`/projects/${id}/custom-sections`, { custom_sections: sections });
      setCustomSections(data.custom_sections);
    } catch { toast.error("Could not save sections"); }
  };

  const addSection = async () => {
    const title = newSection.trim();
    if (!title) return;
    const next = [...customSections.map((c) => ({ title: c.title })), { title }];
    setNewSection("");
    await saveSections(next);
  };

  const removeSection = async (key) => {
    const next = customSections.filter((c) => c.key !== key).map((c) => ({ title: c.title }));
    await saveSections(next);
  };

  const downloadChecklist = async () => {
    try {
      const res = await api.get(`/discovery-checklist.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "Network_Discovery_Checklist.pdf";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download checklist"); }
  };


  const uploadBrief = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploadingBrief(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/projects/${id}/brief-from-document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrief((b) => ({ ...b, ...data.questionnaire }));
      toast.success("Brief pre-filled from document — review & save");
      loadActivity();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read document");
    } finally { setUploadingBrief(false); }
  };

  const [pullingDisc, setPullingDisc] = useState(false);
  const pullFromDiscovery = async () => {
    setPullingDisc(true);
    try {
      const { data } = await api.post(`/projects/${id}/pull-from-discovery`);
      setBrief((b) => ({ ...b, ...data.questionnaire }));
      toast.success("Discovery pulled into the brief — review & save");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not pull the discovery");
    } finally { setPullingDisc(false); }
  };

  const convert = async () => {
    if (!convertPs.trim()) { toast.error("Enter the PS Code"); return; }
    setConverting(true);
    try {
      const { data } = await api.post(`/projects/${id}/convert-to-delivery`, { ps_code: convertPs.trim() });
      toast.success("Converted — opening the new delivery project");
      setConvertOpen(false); setConvertPs("");
      navigate(`/project/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    } finally { setConverting(false); }
  };

  const [asbuiltBusy, setAsbuiltBusy] = useState(false);
  const sendAsbuilt = async () => {
    setAsbuiltBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/asbuilt-handover`);
      toast.success(data.resent ? "As-Built re-sent to Command Centre" : "As-Built handed over to Command Centre");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "As-Built handover failed");
    } finally { setAsbuiltBusy(false); }
  };

  const setStatus = async (status) => {
    setStatusBusy(true);
    try {
      await api.put(`/projects/${id}/opportunity-status`, { status });
      toast.success(`Marked as ${status.toUpperCase()}`);
      load(); loadActivity();
    } catch { toast.error("Could not update status"); }
    finally { setStatusBusy(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/projects/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Client logo added to the pack cover");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Logo upload failed"); }
    finally { setLogoBusy(false); }
  };

  const removeLogo = async () => {
    try { await api.delete(`/projects/${id}/logo`); toast.success("Client logo removed"); load(); }
    catch { toast.error("Could not remove logo"); }
  };

  const loadTemplates = useCallback(async () => {
    try { const { data } = await api.get("/templates"); setTemplates(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    api.get("/users/basic").then(({ data }) => setBasicUsers(data)).catch(() => {});
  }, []);

  const canApprove = isAdmin || (project?.reviewer_id && user?.id === project.reviewer_id);

  const setReviewer = async (rid) => {
    try {
      const { data } = await api.put(`/projects/${id}/reviewer`, { reviewer_id: rid });
      toast.success(`Reviewer set: ${data.reviewer_name || data.reviewer_email}`);
      load(); loadActivity();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set reviewer"); }
  };

  const approveDoc = async (docId, approved) => {
    try {
      await api.post(`/projects/${id}/documents/${docId}/${approved ? "approve" : "unapprove"}`);
      toast.success(approved ? "Document approved" : "Approval revoked");
      load(); loadActivity();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const setRequired = async (docId, required) => {
    try {
      await api.post(`/projects/${id}/documents/${docId}/set-required`, { required });
      toast.success(required ? "Marked required for SOW" : "Marked optional (won't block SOW)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const startProject = async () => {
    try { await api.post(`/projects/${id}/start`); toast.success("Project started — SOW in progress"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
  };

  const genHighlight = async () => {
    setHighlightBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/discovery-highlight`);
      setProject((p) => ({ ...p, discovery_highlight: data.discovery_highlight, suggested_brief: data.suggested_brief }));
      toast.success("Discovery highlight generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to generate"); }
    finally { setHighlightBusy(false); }
  };

  const applySuggested = () => {
    const s = project?.suggested_brief || {};
    const filled = Object.fromEntries(Object.entries(s).filter(([, v]) => v));
    setBrief((b) => ({ ...b, ...filled }));
    toast.success("Suggestions applied to brief — review & Save");
  };

  const runVersionDiff = async () => {
    if (!diffA || !diffB || diffA === diffB) { toast.error("Pick two different versions"); return; }
    try {
      const [a, b] = await Promise.all([
        api.get(`/projects/${id}/versions/${diffA}`),
        api.get(`/projects/${id}/versions/${diffB}`),
      ]);
      const findDoc = (snap) => (snap.snapshot || []).find((s) => s.doc_type === diffDoc);
      const at = findDoc(a.data)?.content || "";
      const bt = findDoc(b.data)?.content || "";
      const parts = diffLines(at, bt);
      const left = [], right = [];
      let adds = 0, dels = 0;
      parts.forEach((p) => {
        const lines = p.value.replace(/\n$/, "").split("\n");
        if (p.added) lines.forEach((l) => { right.push({ t: "add", l }); adds++; });
        else if (p.removed) lines.forEach((l) => { left.push({ t: "del", l }); dels++; });
        else lines.forEach((l) => { left.push({ t: "ctx", l }); right.push({ t: "ctx", l }); });
      });
      setDiffData({ left, right, adds, dels, aLabel: `${a.data.version} ${a.data.stage}`, bLabel: `${b.data.version} ${b.data.stage}` });
    } catch { toast.error("Could not load versions"); }
  };

  const versionDocTypes = (() => {
    // doc types available across saved versions (from current docs as proxy)
    const dbt = {};
    (project?.documents || []).forEach((d) => { dbt[d.doc_type] = d; });
    return docOrder.filter((dt) => dbt[dt]);
  })();

  const isLocked = !!project?.locked;

  const loadWorkflow = useCallback(async () => {
    try {
      const [c, v, a] = await Promise.all([
        api.get(`/projects/${id}/comments`),
        api.get(`/projects/${id}/versions`),
        api.get(`/projects/${id}/attachments`),
      ]);
      setComments(c.data); setVersions(v.data); setAttachments(a.data);
    } catch { /* ignore */ }
  }, [id]);
  useEffect(() => { loadWorkflow(); }, [loadWorkflow]);

  const addComment = async (docType) => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/projects/${id}/comments`, { doc_type: docType, text: newComment.trim() });
      setNewComment("");
      loadWorkflow();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add comment"); }
  };
  const toggleComment = async (cid) => { try { await api.put(`/projects/${id}/comments/${cid}`); loadWorkflow(); } catch { /**/ } };
  const deleteComment = async (cid) => { try { await api.delete(`/projects/${id}/comments/${cid}`); loadWorkflow(); } catch { /**/ } };

  const openComments = comments.filter((c) => !c.resolved).length;

  const reviseAll = async () => {
    if (openComments === 0) { toast.error("Add comments first, then regenerate to apply them"); return; }
    setRevising(true);
    try {
      const { data } = await api.post(`/projects/${id}/revise-async`, {});
      const genId = data.gen_id;
      let status = data.status || "running";
      const started = Date.now();
      let result = null;
      while (status === "running") {
        await new Promise((r) => setTimeout(r, 3000));
        if (Date.now() - started > 300000) throw new Error("Revision timed out");
        const { data: st } = await api.get(`/projects/${id}/generate-status/${genId}`);
        status = st.status;
        if (status === "error") throw new Error(st.error || "Revision failed");
        if (status === "done") result = st.result;
      }
      toast.success(`Revised ${result?.revised?.length ?? 0} document(s) from your comments`);
      load(); loadWorkflow(); loadActivity();
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Revision failed"); }
    finally { setRevising(false); }
  };

  const saveVersion = async () => {
    if (!verForm.version.trim() || !verForm.stage.trim()) { toast.error("Version and stage are required"); return; }
    setSavingVer(true);
    try {
      await api.post(`/projects/${id}/versions`, verForm);
      toast.success(`Saved ${verForm.version} — ${verForm.stage}`);
      setVerOpen(false);
      loadWorkflow(); loadActivity();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save version"); }
    finally { setSavingVer(false); }
  };

  const restoreVersion = async (vid, label) => {
    if (!window.confirm(`Restore ${label}? This replaces the current documents with that version's content.`)) return;
    try {
      await api.post(`/projects/${id}/versions/${vid}/restore`);
      toast.success("Version restored");
      load(); loadActivity();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  const uploadFeedback = async (e, docType) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFbBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("doc_type", docType);
      await api.post(`/projects/${id}/feedback-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Client feedback imported — review the comment and regenerate");
      load(); loadWorkflow(); loadActivity();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setFbBusy(false); }
  };

  const doMarkWon = async () => {
    setWonBusy(true);
    try {
      await api.post(`/projects/${id}/mark-won`, wonForm);
      toast.success("Opportunity WON — baseline locked");
      setWonOpen(false); setWonStep(1);
      load(); loadWorkflow(); loadActivity();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark won"); }
    finally { setWonBusy(false); }
  };

  const unlockProject = async () => {
    if (!window.confirm("Unlock the signed baseline for editing?")) return;
    try { await api.post(`/projects/${id}/unlock`); toast.success("Unlocked"); load(); loadActivity(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unlock failed"); }
  };

  const downloadSelected = async () => {
    const types = Object.keys(dlSel).filter((k) => dlSel[k]);
    if (types.length === 0) { toast.error("Select at least one document"); return; }
    try {
      const res = await api.get(`/projects/${id}/download-all?format=both&types=${types.join(",")}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${project.name}_selected.zip`.replace(/[^a-zA-Z0-9._-]/g, "_");
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      setDlOpen(false);
      toast.success("Selected documents downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
  };

  const applyTemplate = async (tplId) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    setApplyTpl(tplId);
    await saveSections((tpl.custom_sections || []).map((c) => ({ title: c.title })));
    const wanted = new Set([...(tpl.doc_types || []), ...(tpl.custom_sections || []).map((c) => "custom_" + (c.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40))]);
    setSelected((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => { next[k] = false; });
      wanted.forEach((k) => { next[k] = true; });
      return next;
    });
    toast.success(`Applied template "${tpl.name}"`);
  };

  const saveTemplate = async () => {
    const name = window.prompt("Name this template (e.g. Standard Firewall Proposal):");
    if (!name || !name.trim()) return;
    const doc_types = PRESALES_ORDER.filter((k) => selected[k]);
    if (selected["diagram"]) doc_types.push("diagram");
    const custom = customSections.filter((c) => selected[c.key]).map((c) => ({ title: c.title }));
    try {
      await api.post("/templates", { name: name.trim(), doc_types, custom_sections: custom });
      toast.success("Template saved");
      loadTemplates();
    } catch { toast.error("Could not save template"); }
  };

  useEffect(() => {
    api.get(`/projects/${id}/share`).then(({ data }) => {
      setShareEnabled(data.enabled); setShareToken(data.token || "");
    }).catch(() => {});
  }, [id]);

  const shareUrl = shareToken ? `${window.location.origin}/shared/${shareToken}` : "";

  const toggleShare = async () => {
    setShareBusy(true);
    try {
      if (shareEnabled) {
        await api.delete(`/projects/${id}/share`);
        setShareEnabled(false);
        toast.success("Sharing disabled");
      } else {
        const { data } = await api.post(`/projects/${id}/share`);
        setShareEnabled(true); setShareToken(data.token);
        toast.success("Read-only link is live");
      }
    } catch { toast.error("Could not update sharing"); }
    finally { setShareBusy(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
      toast.success("Link copied");
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a config file"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("vendor", vendor);
    fd.append("name", devName);
    fd.append("file", file);
    try {
      await api.post(`/projects/${id}/devices`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Configuration uploaded");
      setFile(null); setDevName("");
      document.getElementById("config-file-input").value = "";
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const deleteDevice = async (did) => {
    try { await api.delete(`/projects/${id}/devices/${did}`); load(); toast.success("Device removed"); }
    catch { toast.error("Delete failed"); }
  };

  const saveBrief = async () => {
    setSavingBrief(true);
    try {
      await api.put(`/projects/${id}/questionnaire`, { questionnaire: brief });
      toast.success("Project brief saved");
      load(); loadActivity();
    } catch { toast.error("Could not save brief"); }
    finally { setSavingBrief(false); }
  };

  const calcSizing = async () => {
    setSizingBusy(true);
    try {
      const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
      const payload = {
        users: numOrNull(sizing.users),
        circuit_speed_mbps: numOrNull(sizing.circuit_speed_mbps),
        placement: sizing.placement,
        ssl_mode: sizing.ssl_mode,
        utm_level: sizing.utm_level,
        growth_headroom_pct: sizing.growth_headroom_pct === "" ? 30 : Number(sizing.growth_headroom_pct),
        ha: !!sizing.ha,
        ra_vpn_users: numOrNull(sizing.ra_vpn_users),
        s2s_tunnels: numOrNull(sizing.s2s_tunnels),
        req_ports: {
          "1g": numOrNull(sizing.req_ports?.["1g"]) || 0,
          "10g": numOrNull(sizing.req_ports?.["10g"]) || 0,
          "25g": numOrNull(sizing.req_ports?.["25g"]) || 0,
        },
        logs_per_second: numOrNull(sizing.logs_per_second),
        log_gb_per_day: numOrNull(sizing.log_gb_per_day),
        retention_days: numOrNull(sizing.retention_days),
        firmware_train: sizing.firmware_train,
        firmware_maturity: sizing.firmware_maturity,
      };
      const { data } = await api.put(`/projects/${id}/sizing`, payload);
      setSizingResult(data);
      if (data.recommended_model) toast.success(`Recommended: ${data.recommended_model}`);
      else toast.error(data.rationale || "Add users or throughput to size");
      loadActivity();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not calculate sizing"); }
    finally { setSizingBusy(false); }
  };

  const saveSkus = async () => {
    setSavingSkus(true);
    try {
      const lines = skuCatalog
        .filter((it) => skuState[it.id]?.checked)
        .map((it) => ({ sku: it.sku, description: it.description, quantity: Number(skuState[it.id]?.qty || 0) }));
      await api.put(`/projects/${id}/skus`, { lines });
      toast.success(`Saved ${lines.filter((l) => l.quantity >= 1).length} SKU line(s)`);
      loadActivity();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save SKUs"); }
    finally { setSavingSkus(false); }
  };

  const [genProgress, setGenProgress] = useState("");
  const generate = async () => {
    const doc_types = genOrder.filter((d) => selected[d]);
    if (doc_types.length === 0) { toast.error("Select at least one item to generate"); return; }
    if (!isPresales && (!project.devices || project.devices.length === 0)) { toast.error("Upload at least one configuration first"); return; }
    if (isPresales && !brief.requirements.trim() && !brief.sites.trim() && !brief.vendors.trim()) {
      toast.error("Fill in the project brief first (requirements, sites or vendors)"); return;
    }
    setGenerating(true);
    let done = 0;
    try {
      // Generate one document per request, async + poll, to survive edge timeouts on long AI runs.
      for (const dt of doc_types) {
        setGenProgress(`${metaFor(dt)?.full || dt} (${done + 1}/${doc_types.length})`);
        const { data } = await api.post(`/projects/${id}/generate-async`, { doc_types: [dt] });
        const genId = data.gen_id;
        let status = data.status || "running";
        const started = Date.now();
        while (status === "running") {
          await new Promise((r) => setTimeout(r, 3000));
          if (Date.now() - started > 300000) throw new Error("Generation timed out");
          const { data: st } = await api.get(`/projects/${id}/generate-status/${genId}`);
          status = st.status;
          if (status === "error") throw new Error(st.error || "Generation failed");
        }
        done += 1;
        await load();
      }
      toast.success(`Generated ${done} item(s)`);
      loadActivity();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || `Generation failed after ${done} item(s)`);
      loadActivity();
    } finally { setGenerating(false); setGenProgress(""); }
  };

  const openDoc = async (doc) => {
    setViewDoc(doc); setLoadingDoc(true); setViewContent(""); setEditing(false);
    try {
      const { data } = await api.get(`/projects/${id}/documents/${doc.id}`);
      setViewContent(data.content);
    } catch { toast.error("Could not load document"); }
    finally { setLoadingDoc(false); }
  };

  const saveDoc = async () => {
    setSavingDoc(true);
    try {
      await api.put(`/projects/${id}/documents/${viewDoc.id}`, { content: editContent });
      setViewContent(editContent); setEditing(false);
      toast.success("Document updated");
      load(); loadActivity();
    } catch { toast.error("Save failed"); }
    finally { setSavingDoc(false); }
  };

  const download = async (doc, format) => {
    try {
      const res = await api.get(`/projects/${id}/documents/${doc.id}/download?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "docx" ? "docx" : "pdf";
      a.download = `${project.name}_${doc.doc_type}.${ext}`.replace(/[^a-zA-Z0-9._-]/g, "_");
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error("Download failed"); }
  };

  const [downloadingAll, setDownloadingAll] = useState(false);
  const downloadAll = async () => {
    setDownloadingAll(true);
    try {
      const res = await api.get(`/projects/${id}/download-all?format=both`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.name}_documents.zip`.replace(/[^a-zA-Z0-9._-]/g, "_");
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("All documents downloaded (ZIP)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    finally { setDownloadingAll(false); }
  };

  if (loading) return <div className="p-10 font-mono-jb text-[#8B949E]">Loading...</div>;
  if (!project) return <div className="p-10 font-mono-jb text-[#8B949E]">Project not found.</div>;

  const docsByType = {};
  (project.documents || []).forEach((d) => { docsByType[d.doc_type] = d; });
  const handoffDocs = (project.documents || []).filter((d) => d.handoff);
  const approvableDocs = isPresales ? (project.documents || []).filter((d) => !NON_APPROVAL_DOC_TYPES.includes(d.doc_type) && d.sow_required !== false) : [];
  const approvedCount = approvableDocs.filter((d) => d.approved).length;

  return (
    <div className="p-8 lg:p-10">
      <Link to="/" data-testid="back-to-projects" className="inline-flex items-center gap-2 text-xs font-mono-jb uppercase tracking-widest text-[#8B949E] hover:text-[#00F0FF] transition-colors duration-200 mb-6">
        <ArrowLeft size={14} /> All Projects
      </Link>

      {isLocked && (
        <div data-testid="locked-banner" className="mb-6 flex items-center gap-3 border border-[#00FF41]/40 bg-[#00FF41]/5 px-4 py-3">
          <Lock size={18} className="text-[#00FF41] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-mono-jb text-sm text-[#00FF41]">Baseline SOW locked — WON</div>
            <div className="text-[11px] text-[#8B949E] font-mono-jb">This signed baseline is read-only. Documents can't be edited, revised or regenerated.</div>
          </div>
          {isAdmin && (
            <Button data-testid="unlock-button" onClick={unlockProject} variant="outline"
              className="rounded-sm font-mono-jb uppercase text-[10px] tracking-wider h-9 border-[#EAE04A]/50 bg-transparent text-[#EAE04A] hover:bg-[#EAE04A]/10 hover:text-[#EAE04A]">
              <Unlock size={13} className="mr-1" /> Unlock
            </Button>
          )}
        </div>
      )}

      <div className="border-b border-white/10 pb-6 mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#484F58] mb-2 font-mono-jb">{project.client || "Project"}</div>
          <div className="flex items-center gap-2 mb-1">
            <span data-testid="project-detail-type" className={`text-[9px] uppercase tracking-[0.15em] font-mono-jb px-1.5 py-0.5 rounded-sm border ${isPresales ? "text-[#EAE04A] border-[#EAE04A]/30 bg-[#EAE04A]/10" : isAssessment ? "text-[#FF7A59] border-[#FF7A59]/30 bg-[#FF7A59]/10" : "text-[#00F0FF] border-[#00F0FF]/30 bg-[#00F0FF]/10"}`}>
              {isPresales ? "Pre-Sales" : isAssessment ? "Assessment" : "Delivery"}
            </span>
          </div>
          <h1 className="font-mono-jb text-3xl sm:text-4xl font-extrabold tracking-tighter text-white">{project.name}</h1>
          {project.ps_code && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono-jb text-[#00FF41] border border-[#00FF41]/25 rounded-sm px-2 py-1" data-testid="project-detail-pscode">
              PS Code: {project.ps_code}
            </div>
          )}
          {project.description && <p className="text-[#8B949E] mt-3 max-w-3xl leading-relaxed">{project.description}</p>}
          {isPresales && (
            <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="status-control">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#484F58] font-mono-jb mr-1">Opportunity:</span>
              {[
                { k: "open", label: "Open", icon: CircleDot, on: "border-[#EAE04A] bg-[#EAE04A]/15 text-[#EAE04A]" },
                { k: "lost", label: "Lost", icon: XCircle, on: "border-[#FF3B30] bg-[#FF3B30]/15 text-[#FF3B30]" },
              ].map(({ k, label, icon: Icon, on }) => {
                const active = (project.status || "open") === k;
                return (
                  <button key={k} data-testid={`status-${k}`} disabled={statusBusy || project.status === "won"} onClick={() => setStatus(k)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm border font-mono-jb text-[11px] uppercase tracking-wider transition-colors duration-200 disabled:opacity-40 ${active ? on : "border-white/15 text-[#8B949E] hover:border-white/30"}`}>
                    <Icon size={12} /> {label}
                  </button>
                );
              })}
              {project.status === "won" ? (
                <span data-testid="status-won-badge" className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-[#00FF41] bg-[#00FF41]/15 text-[#00FF41] font-mono-jb text-[11px] uppercase tracking-wider">
                  <Trophy size={12} /> Won
                </span>
              ) : (
                <span className="text-[10px] text-[#484F58] font-mono-jb ml-1">Win via “Mark as WON” (needs all docs approved)</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {isPresales && project.status !== "won" && !isLocked && (
            <Button data-testid="mark-won-button" onClick={() => { setWonStep(1); setWonOpen(true); }}
              className="rounded-sm font-mono-jb uppercase text-xs tracking-widest h-10 bg-[#00FF41] text-black font-bold hover:bg-[#00FF41]">
              <Trophy size={15} className="mr-1.5" /> Mark as WON
            </Button>
          )}
          {isPresales && (
            <Button data-testid="convert-to-delivery-button" onClick={() => setConvertOpen(true)} variant="outline"
              className="rounded-sm font-mono-jb uppercase text-xs tracking-widest h-10 border-[#EAE04A]/50 bg-[#EAE04A]/10 text-[#EAE04A] hover:bg-[#EAE04A]/20 hover:text-[#EAE04A] transition-colors duration-200">
              <ArrowRightLeft size={15} className="mr-1.5" /> Convert to Delivery
            </Button>
          )}
          {project.project_type === "delivery" && project.cc_opportunity_id && (
            <Button data-testid="asbuilt-handover-button" onClick={sendAsbuilt} disabled={asbuiltBusy}
              className={`rounded-sm font-mono-jb uppercase text-xs tracking-widest h-10 font-bold transition-colors duration-200 ${project.asbuilt_fired ? "bg-transparent border border-[#00FF41]/50 text-[#00FF41] hover:bg-[#00FF41]/10" : "bg-[#00FF41] text-black hover:bg-[#00FF41]"}`}>
              <ClipboardCheck size={15} className="mr-1.5" />
              {asbuiltBusy ? "Sending…" : project.asbuilt_fired ? "As-Built Sent ✓ — Re-send" : "Send As-Built to CC"}
            </Button>
          )}
          <Button data-testid="share-button" onClick={() => setShareOpen(true)} variant="outline"
            className={`rounded-sm font-mono-jb uppercase text-xs tracking-widest h-10 transition-colors duration-200 ${shareEnabled ? "border-[#00FF41]/50 bg-[#00FF41]/10 text-[#00FF41] hover:bg-[#00FF41]/20 hover:text-[#00FF41]" : "border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#00F0FF]"}`}>
            <Share2 size={15} className="mr-1.5" /> {shareEnabled ? "Shared" : "Share"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: brief (presales) / devices (delivery) */}
        <div className="lg:col-span-1 space-y-6">
          {isPresales && (
            <section className="border border-white/10 bg-[#0B0C10] p-6" data-testid="brief-section">
              <div className="flex items-center gap-2 mb-5">
                <ClipboardList className="text-[#EAE04A]" size={18} />
                <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Project Brief</h2>
              </div>
              <label data-testid="upload-rfp-label" className={`flex items-center justify-center gap-2 border border-dashed ${uploadingBrief ? "border-[#EAE04A]/60 bg-[#EAE04A]/5" : "border-white/20 hover:border-[#EAE04A]/50"} rounded-sm py-3 mb-4 cursor-pointer transition-colors duration-200`}>                {uploadingBrief
                  ? <><Loader2 className="animate-spin text-[#EAE04A]" size={15} /> <span className="font-mono-jb text-[11px] text-[#EAE04A]">Reading document...</span></>
                  : <><FileUp size={15} className="text-[#EAE04A]" /> <span className="font-mono-jb text-[11px] text-[#8B949E]">Upload RFP / brief — AI pre-fills the form</span></>}
                <input data-testid="upload-rfp-input" type="file" accept=".pdf,.docx,.txt,.md,.doc" className="hidden" onChange={uploadBrief} disabled={uploadingBrief} />
              </label>
              <a data-testid="download-checklist" href={`${API}/projects/discovery-checklist.pdf`.replace('/projects','')} onClick={(e) => { e.preventDefault(); downloadChecklist(); }}
                className="flex items-center justify-center gap-2 border border-white/15 hover:border-[#00F0FF]/50 rounded-sm py-2.5 mb-4 cursor-pointer transition-colors duration-200 font-mono-jb text-[11px] text-[#00F0FF]">
                <Download size={14} /> Download Blank Discovery Checklist (PDF)
              </a>
              {isPresales && project.cc_opportunity_id && !isLocked && (
                <button data-testid="pull-from-discovery-btn" onClick={pullFromDiscovery} disabled={pullingDisc}
                  className="w-full flex items-center justify-center gap-2 border border-[#00F0FF]/25 bg-[#00F0FF]/5 hover:border-[#00F0FF]/50 rounded-sm py-2.5 mb-4 cursor-pointer transition-colors duration-200 font-mono-jb text-[11px] text-[#00F0FF]">
                  {pullingDisc ? <Loader2 size={14} className="animate-spin" /> : <Compass size={14} />}
                  Pull discovery detail into brief
                </button>
              )}
              {project.started === false && (
                <div data-testid="start-project-banner" className="mb-4 border border-[#EAE04A]/40 bg-[#EAE04A]/10 rounded-sm p-3 flex items-center justify-between gap-3">
                  <div className="text-[11px] font-mono-jb text-[#EAE04A]">Confirmed by client — not started yet. Start to move it to SOW in progress.</div>
                  <Button data-testid="start-project-button" onClick={startProject} className="h-8 rounded-sm bg-[#EAE04A] text-black font-mono-jb text-[10px] uppercase tracking-wider hover:bg-[#EAE04A]">
                    <Play size={13} className="mr-1" /> Start
                  </Button>
                </div>
              )}
              <div data-testid="discovery-highlight-card" className="mb-4 border border-[#B084FF]/30 bg-[#B084FF]/5 rounded-sm p-3">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#B084FF] font-mono-jb whitespace-nowrap"><Sparkles size={12} /> Discovery Highlight (AI)</div>
                  <div className="flex items-center gap-2 ml-auto">
                    {project.suggested_brief && Object.keys(project.suggested_brief).length > 0 && !isLocked && (
                      <Button data-testid="apply-suggestions-button" onClick={applySuggested} variant="outline" className="h-7 rounded-sm bg-transparent border-[#00F0FF]/40 text-[#00F0FF] font-mono-jb text-[9px] uppercase tracking-wider hover:bg-[#00F0FF]/10">Apply to brief</Button>
                    )}
                    <Button data-testid="gen-highlight-button" onClick={genHighlight} disabled={highlightBusy} variant="outline" className="h-7 rounded-sm bg-transparent border-[#B084FF]/40 text-[#B084FF] font-mono-jb text-[9px] uppercase tracking-wider hover:bg-[#B084FF]/10">
                      {highlightBusy ? <Loader2 size={12} className="animate-spin" /> : <><RotateCw size={11} className="mr-1" />{project.discovery_highlight ? "Regenerate" : "Generate"}</>}
                    </Button>
                  </div>
                </div>
                {project.discovery_highlight ? (
                  <div data-testid="discovery-highlight-text" className="text-[12px] font-mono-jb text-[#C9D1D9] whitespace-pre-wrap leading-relaxed">{project.discovery_highlight}</div>
                ) : (
                  <div className="text-[11px] font-mono-jb text-[#8B949E]">Generate an AI summary of the discovery notes + client intake to guide the brief & sizing.</div>
                )}
                {project.client_intake && Object.keys(project.client_intake).length > 0 && (
                  <details data-testid="client-intake-details" className="mt-2">
                    <summary className="text-[10px] font-mono-jb text-[#8B949E] cursor-pointer hover:text-white">Client intake answers</summary>
                    <div className="mt-1 space-y-0.5">
                      {Object.entries(project.client_intake).filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="text-[11px] font-mono-jb text-[#C9D1D9]"><span className="text-[#8B949E]">{k.replace(/_/g, " ")}:</span> {String(v)}</div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Sites / Locations</label>
                  <Textarea data-testid="brief-sites" disabled={isLocked} value={brief.sites} onChange={(e) => setBrief({ ...brief, sites: e.target.value })}
                    placeholder="e.g. HQ Sydney + 3 branch sites, 1 DC" className="mt-2 min-h-[64px] bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#EAE04A]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Business & Technical Requirements</label>
                  <Textarea data-testid="brief-requirements" disabled={isLocked} value={brief.requirements} onChange={(e) => setBrief({ ...brief, requirements: e.target.value })}
                    placeholder="What the client needs — SD-WAN, HA firewalls, secure remote access..." className="mt-2 min-h-[96px] bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#EAE04A]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Proposed Vendors / Devices</label>
                  <Textarea data-testid="brief-vendors" disabled={isLocked} value={brief.vendors} onChange={(e) => setBrief({ ...brief, vendors: e.target.value })}
                    placeholder="e.g. 2x FortiGate 200F (HA), FortiManager, FortiAnalyzer" className="mt-2 min-h-[64px] bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#EAE04A]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Constraints</label>
                  <Textarea data-testid="brief-constraints" disabled={isLocked} value={brief.constraints} onChange={(e) => setBrief({ ...brief, constraints: e.target.value })}
                    placeholder="Budget, existing kit to reuse, technical limitations..." className="mt-2 min-h-[56px] bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#EAE04A]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Dependencies</label>
                  <Textarea data-testid="brief-dependencies" disabled={isLocked} value={brief.dependencies} onChange={(e) => setBrief({ ...brief, dependencies: e.target.value })}
                    placeholder="Client prerequisites, third parties, circuits, access..." className="mt-2 min-h-[56px] bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#EAE04A]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Timeline</label>
                  <Textarea data-testid="brief-timeline" disabled={isLocked} value={brief.timeline} onChange={(e) => setBrief({ ...brief, timeline: e.target.value })}
                    placeholder="Target dates, cutover windows, phasing..." className="mt-2 min-h-[56px] bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#EAE04A]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Additional Notes (optional)</label>
                  <Textarea data-testid="brief-notes" disabled={isLocked} value={brief.notes} onChange={(e) => setBrief({ ...brief, notes: e.target.value })}
                    placeholder="Anything else the AI should factor in" className="mt-2 min-h-[52px] bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#EAE04A]" />
                </div>
                <Button data-testid="save-brief-button" onClick={saveBrief} disabled={savingBrief || isLocked}
                  className="w-full rounded-sm bg-[#EAE04A] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#EAE04A]">
                  {savingBrief ? <Loader2 className="animate-spin" size={16} /> : <><Save size={15} className="mr-1" /> Save Brief</>}
                </Button>
              </div>
            </section>
          )}
          {isPresales && (
            <section className="border border-white/10 bg-[#0B0C10] p-6" data-testid="sizing-section">
              <div className="flex items-center gap-2 mb-2">
                <Cpu className="text-[#00F0FF]" size={18} />
                <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">FortiGate Sizing</h2>
              </div>
              <div className="text-[11px] text-[#8B949E] font-mono-jb mb-4">Datasheet-backed model pick + firmware advisory. Feeds the BoM, Solution & HLD.</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Placement Scenario</label>
                  <Select value={sizing.placement} onValueChange={(v) => setSizing({ ...sizing, placement: v })}>
                    <SelectTrigger data-testid="sizing-placement" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0C10] border-white/15 font-mono-jb text-sm">
                      <SelectItem value="edge">Internet Edge</SelectItem>
                      <SelectItem value="datacenter">Data Centre</SelectItem>
                      <SelectItem value="internal">Internal Segmentation</SelectItem>
                      <SelectItem value="branch">Branch / SD-WAN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">ISP Circuit Speed</label>
                  <Select value={String(sizing.circuit_speed_mbps || "")} onValueChange={(v) => setSizing({ ...sizing, circuit_speed_mbps: v })}>
                    <SelectTrigger data-testid="sizing-circuit" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm"><SelectValue placeholder="Select circuit" /></SelectTrigger>
                    <SelectContent className="bg-[#0B0C10] border-white/15 font-mono-jb text-sm">
                      <SelectItem value="100">100 Mbps</SelectItem>
                      <SelectItem value="250">250 Mbps</SelectItem>
                      <SelectItem value="500">500 Mbps</SelectItem>
                      <SelectItem value="1000">1 Gbps</SelectItem>
                      <SelectItem value="2000">2 Gbps</SelectItem>
                      <SelectItem value="5000">5 Gbps</SelectItem>
                      <SelectItem value="10000">10 Gbps</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Concurrent Users</label>
                  <Input data-testid="sizing-users" type="number" min="0" value={sizing.users}
                    onChange={(e) => setSizing({ ...sizing, users: e.target.value })}
                    placeholder="e.g. 250" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Growth Headroom</label>
                  <Select value={String(sizing.growth_headroom_pct)} onValueChange={(v) => setSizing({ ...sizing, growth_headroom_pct: v })}>
                    <SelectTrigger data-testid="sizing-headroom" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0C10] border-white/15 font-mono-jb text-sm">
                      <SelectItem value="15">15% (conservative)</SelectItem>
                      <SelectItem value="30">30% (standard)</SelectItem>
                      <SelectItem value="50">50% (aggressive)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">UTM Profile</label>
                  <Select value={sizing.utm_level} onValueChange={(v) => setSizing({ ...sizing, utm_level: v })}>
                    <SelectTrigger data-testid="sizing-utm" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0C10] border-white/15 font-mono-jb text-sm">
                      <SelectItem value="threat_protection">Full UTM (Threat Protection)</SelectItem>
                      <SelectItem value="ngfw">NGFW (IPS + App Ctrl)</SelectItem>
                      <SelectItem value="ips">IPS only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">SSL/TLS Inspection</label>
                  <Select value={sizing.ssl_mode} onValueChange={(v) => setSizing({ ...sizing, ssl_mode: v })}>
                    <SelectTrigger data-testid="sizing-ssl-mode" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0C10] border-white/15 font-mono-jb text-sm">
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="certificate">Certificate / SNI only</SelectItem>
                      <SelectItem value="deep">Deep Packet Inspection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">FortiOS Train</label>
                  <Select value={sizing.firmware_train} onValueChange={(v) => setSizing({ ...sizing, firmware_train: v })}>
                    <SelectTrigger data-testid="sizing-firmware" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0C10] border-white/15 font-mono-jb text-sm">
                      <SelectItem value="7.4">FortiOS 7.4 (mature)</SelectItem>
                      <SelectItem value="7.6">FortiOS 7.6 (recommended)</SelectItem>
                      <SelectItem value="8.0">FortiOS 8.0 (newest)</SelectItem>
                      <SelectItem value="7.2">FortiOS 7.2 (legacy)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Deployment Stability</label>
                  <Select value={sizing.firmware_maturity} onValueChange={(v) => setSizing({ ...sizing, firmware_maturity: v })}>
                    <SelectTrigger data-testid="sizing-maturity" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0C10] border-white/15 font-mono-jb text-sm">
                      <SelectItem value="mature">Mature (GA.M)</SelectItem>
                      <SelectItem value="feature">Feature (newest)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-4 text-[10px] tracking-[0.2em] uppercase text-[#00F0FF] font-mono-jb">VPN Requirements</div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Remote-Access VPN Users</label>
                  <Input data-testid="sizing-ravpn" type="number" min="0" value={sizing.ra_vpn_users}
                    onChange={(e) => setSizing({ ...sizing, ra_vpn_users: e.target.value })}
                    placeholder="concurrent" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Site-to-Site IPsec Tunnels</label>
                  <Input data-testid="sizing-s2s" type="number" min="0" value={sizing.s2s_tunnels}
                    onChange={(e) => setSizing({ ...sizing, s2s_tunnels: e.target.value })}
                    placeholder="tunnels" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
                </div>
              </div>

              <div className="mt-4 text-[10px] tracking-[0.2em] uppercase text-[#00F0FF] font-mono-jb">Required Ports (min counts)</div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">1GbE</label>
                  <Input data-testid="sizing-ports-1g" type="number" min="0" value={sizing.req_ports["1g"]}
                    onChange={(e) => setSizing({ ...sizing, req_ports: { ...sizing.req_ports, "1g": e.target.value } })}
                    className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">10GbE SFP+</label>
                  <Input data-testid="sizing-ports-10g" type="number" min="0" value={sizing.req_ports["10g"]}
                    onChange={(e) => setSizing({ ...sizing, req_ports: { ...sizing.req_ports, "10g": e.target.value } })}
                    className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">25GbE SFP28</label>
                  <Input data-testid="sizing-ports-25g" type="number" min="0" value={sizing.req_ports["25g"]}
                    onChange={(e) => setSizing({ ...sizing, req_ports: { ...sizing.req_ports, "25g": e.target.value } })}
                    className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
                </div>
              </div>

              <div className="mt-4 text-[10px] tracking-[0.2em] uppercase text-[#00F0FF] font-mono-jb">Logging & Retention (for BoM)</div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Logs / sec</label>
                  <Input data-testid="sizing-lps" type="number" min="0" value={sizing.logs_per_second}
                    onChange={(e) => setSizing({ ...sizing, logs_per_second: e.target.value })}
                    placeholder="LPS" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">or GB / day</label>
                  <Input data-testid="sizing-gbday" type="number" min="0" value={sizing.log_gb_per_day}
                    onChange={(e) => setSizing({ ...sizing, log_gb_per_day: e.target.value })}
                    placeholder="GB/day" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Retention (days)</label>
                  <Input data-testid="sizing-retention" type="number" min="0" value={sizing.retention_days}
                    onChange={(e) => setSizing({ ...sizing, retention_days: e.target.value })}
                    placeholder="e.g. 365" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer mt-4">
                <Switch data-testid="sizing-ha" checked={sizing.ha} onCheckedChange={(v) => setSizing({ ...sizing, ha: v })} />
                <span className="text-[11px] text-[#8B949E] font-mono-jb uppercase tracking-wider">HA Pair (active/passive)</span>
              </label>
              <Button data-testid="calc-sizing-button" onClick={calcSizing} disabled={sizingBusy || isLocked}
                className="w-full mt-4 rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF]">
                {sizingBusy ? <Loader2 className="animate-spin" size={16} /> : <><Cpu size={15} className="mr-1" /> Calculate Recommendation</>}
              </Button>

              {sizingResult && (
                <div data-testid="sizing-result" className="mt-5 border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-4">
                  {sizingResult.recommended_model ? (
                    <>
                      <div className="flex items-baseline justify-between">
                        <div className="font-mono-jb text-lg text-[#00F0FF] font-bold" data-testid="sizing-model">{sizingResult.recommended_model}</div>
                        {sizingResult.alternative_model && <div className="text-[10px] text-[#8B949E] font-mono-jb">next up: {sizingResult.alternative_model}</div>}
                      </div>
                      <div className="text-[11px] text-[#C9D1D9] mt-2 leading-relaxed">{sizingResult.rationale}</div>
                      {sizingResult.utilization && Object.keys(sizingResult.utilization).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {Object.entries(sizingResult.utilization).map(([k, v]) => (
                            <span key={k} className={`text-[10px] font-mono-jb px-2 py-0.5 border ${v > 90 ? "border-[#FF3B30]/50 text-[#FF3B30]" : "border-white/20 text-[#8B949E]"}`}>{k}: {v}%</span>
                          ))}
                        </div>
                      )}
                      {sizingResult.ha_note && <div className="text-[11px] text-[#EAE04A] mt-2 font-mono-jb">{sizingResult.ha_note}</div>}
                      {sizingResult.ports && (
                        <div className="mt-3 pt-3 border-t border-white/10" data-testid="sizing-ports">
                          <div className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb mb-1">Physical Connectivity</div>
                          <div className="text-[11px] text-[#C9D1D9]">Required 1GbE×{sizingResult.ports.required["1g"]}, 10GbE×{sizingResult.ports.required["10g"]}, 25GbE×{sizingResult.ports.required["25g"]} — model provides: {sizingResult.ports.interfaces}</div>
                        </div>
                      )}
                      {sizingResult.logging && (
                        <div className="mt-3 pt-3 border-t border-white/10" data-testid="sizing-logging">
                          <div className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb mb-1">Logging / Retention</div>
                          <div className="text-[11px] text-[#C9D1D9]">~{sizingResult.logging.gb_per_day} GB/day × {sizingResult.logging.retention_days} days ⇒ ~{Math.round(sizingResult.logging.required_storage_gb)} GB</div>
                          <div className="text-[11px] text-[#00F0FF] mt-0.5">→ {sizingResult.logging.recommendation} <span className="text-[#8B949E]">(add to BoM)</span></div>
                        </div>
                      )}
                      {sizingResult.firmware_advisory && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <div className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb mb-1">Firmware & Support</div>
                          <div className="text-[11px] text-[#C9D1D9]">FortiOS {sizingResult.firmware_advisory.train} ({sizingResult.firmware_advisory.maturity}) — {sizingResult.firmware_advisory.recommended_patch}. EoS {sizingResult.firmware_advisory.end_of_support} (~{sizingResult.firmware_advisory.months_to_eos} mo).</div>
                          {(sizingResult.firmware_advisory.warnings || []).map((w, i) => (
                            <div key={i} data-testid={`firmware-warning-${i}`} className="text-[11px] text-[#FF3B30] mt-1 flex gap-1"><XCircle size={13} className="shrink-0 mt-0.5" />{w}</div>
                          ))}
                        </div>
                      )}
                      {sizingResult.psirt && sizingResult.psirt.alerts && sizingResult.psirt.alerts.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/10" data-testid="psirt-alerts">
                          <div className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb mb-1">Live PSIRT Advisories</div>
                          {sizingResult.psirt.alerts.slice(0, 5).map((a, i) => (
                            <a key={i} href={a.link} target="_blank" rel="noreferrer" className="block text-[11px] text-[#EAE04A] hover:underline mt-1 truncate">
                              {a.severity ? `[${a.severity}] ` : ""}{a.title}
                            </a>
                          ))}
                        </div>
                      )}
                      {sizingResult.needs_se_review && <div className="text-[11px] text-[#EAE04A] mt-2 font-mono-jb">⚠️ Borderline — confirm with a Fortinet SE.</div>}
                    </>
                  ) : (
                    <div className="text-[11px] text-[#EAE04A] font-mono-jb">{sizingResult.rationale}</div>
                  )}
                </div>
              )}
            </section>
          )}

          {isPresales && (
            <section className="border border-white/10 bg-[#0B0C10] p-6" data-testid="skus-section">
              <div className="flex items-center gap-2 mb-2">
                <ListChecks className="text-[#00F0FF]" size={18} />
                <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Standard Services &amp; SKUs</h2>
              </div>
              <div className="text-[11px] text-[#8B949E] font-mono-jb mb-4">Tick the professional-services line items and set quantities. Lines with a quantity are appended to the Bill of Materials.</div>
              {skuCatalog.length === 0 ? (
                <div className="text-[11px] text-[#8B949E] font-mono-jb py-2">No standard SKUs configured. An admin can add them in Settings.</div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(skuCatalog.reduce((acc, it) => {
                    const cat = it.category || "Other";
                    (acc[cat] = acc[cat] || []).push(it);
                    return acc;
                  }, {})).map(([cat, items]) => (
                    <div key={cat} data-testid={`sku-cat-${cat.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] font-mono-jb mb-2 pb-1 border-b border-[#00F0FF]/20">{cat}</div>
                      <div className="space-y-2">
                        {items.map((it) => {
                          const st = skuState[it.id] || { checked: false, qty: it.default_qty || 0 };
                          return (
                            <div key={it.id} data-testid={`sku-row-${it.id}`} className={`flex items-start gap-3 border p-3 rounded-sm ${st.checked ? "border-[#00F0FF]/30 bg-[#00F0FF]/5" : "border-white/10"}`}>
                              <input type="checkbox" data-testid={`sku-check-${it.id}`} checked={!!st.checked}
                                onChange={(e) => setSkuState({ ...skuState, [it.id]: { ...st, checked: e.target.checked } })}
                                className="mt-1 accent-[#00F0FF] w-4 h-4 cursor-pointer" />
                              <div className="flex-1 min-w-0">
                                <div className="font-mono-jb text-[12px] text-white">{it.sku}</div>
                                <div className="text-[11px] text-[#8B949E] leading-snug">{it.description}</div>
                              </div>
                              <Input data-testid={`sku-qty-${it.id}`} type="number" min="0" value={st.qty}
                                disabled={!st.checked}
                                onChange={(e) => setSkuState({ ...skuState, [it.id]: { ...st, qty: e.target.value } })}
                                className="w-20 shrink-0 bg-[#0B0C10] border-white/15 font-mono-jb text-sm text-center focus-visible:ring-[#00F0FF] disabled:opacity-40" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <Button data-testid="save-skus-button" onClick={saveSkus} disabled={savingSkus || isLocked}
                    className="w-full mt-3 rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF]">
                    {savingSkus ? <Loader2 className="animate-spin" size={16} /> : <><Save size={15} className="mr-1" /> Save SKUs</>}
                  </Button>
                </div>
              )}
            </section>
          )}


          {!isPresales && (
          <>
          <section className="border border-white/10 bg-[#0B0C10] p-6">
            <div className="flex items-center gap-2 mb-5">
              <UploadCloud className="text-[#00F0FF]" size={18} />
              <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Upload Config</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Vendor</label>
                <Select value={vendor} onValueChange={setVendor}>
                  <SelectTrigger data-testid="vendor-select" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus:ring-[#00F0FF]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B0C10] border-white/15 text-white">
                    {Object.entries(VENDOR_META).map(([k, v]) => (
                      <SelectItem key={k} value={k} data-testid={`vendor-option-${k}`} className="font-mono-jb text-sm">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: v.color }} />{v.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Device Name (optional)</label>
                <Input data-testid="device-name-input" value={devName} onChange={(e) => setDevName(e.target.value)}
                  placeholder="fgt-core-01" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Config File</label>
                <input id="config-file-input" data-testid="config-file-input" type="file" accept=".txt,.conf,.cfg,.log,.text"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="mt-2 block w-full text-xs text-[#8B949E] font-mono-jb file:mr-3 file:py-2 file:px-3 file:border file:border-[#00F0FF]/40 file:bg-[#00F0FF]/10 file:text-[#00F0FF] file:font-mono-jb file:uppercase file:text-[10px] file:tracking-widest file:cursor-pointer hover:file:bg-[#00F0FF]/20" />
              </div>
              <Button data-testid="upload-config-button" onClick={upload} disabled={uploading}
                className="w-full rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF] hover:glow-cyan transition-colors duration-200">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <><UploadCloud size={15} className="mr-1" /> Upload</>}
              </Button>
            </div>
          </section>

          <section className="border border-white/10 bg-[#0B0C10] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Server className="text-[#00FF41]" size={18} />
              <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Devices ({project.devices?.length || 0})</h2>
            </div>
            {(!project.devices || project.devices.length === 0) ? (
              <div className="text-[#484F58] text-sm font-mono-jb py-4">No configs uploaded.</div>
            ) : (
              <div className="space-y-2" data-testid="devices-list">
                {project.devices.map((d) => {
                  const meta = VENDOR_META[d.vendor] || VENDOR_META.other;
                  return (
                    <div key={d.id} data-testid={`device-row-${d.id}`}
                      className="flex items-center gap-3 border border-white/10 px-3 py-2.5 hover:border-white/25 transition-colors duration-200">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: meta.color }} />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono-jb text-sm text-white truncate">{d.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#8B949E]" style={{ color: meta.color }}>{meta.label}</div>
                      </div>
                      <button data-testid={`delete-device-${d.id}`} onClick={() => deleteDevice(d.id)}
                        className="text-[#484F58] hover:text-[#FF3B30] transition-colors duration-200"><Trash2 size={15} /></button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          </>
          )}
        </div>

        {/* RIGHT: generate + docs */}
        <div className="lg:col-span-2 space-y-6">
          {!isPresales && handoffDocs.length > 0 && (
            <section className="border border-[#EAE04A]/25 bg-[#EAE04A]/5 p-6" data-testid="handoff-section">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardCheck className="text-[#EAE04A]" size={18} />
                <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Pre-Sales Handoff</h2>
                <span className="font-mono-jb text-[10px] text-[#484F58] uppercase tracking-wider">signed pack · read-only reference</span>
              </div>
              <div className="space-y-2" data-testid="handoff-list">
                {handoffDocs.map((doc) => (
                  <div key={doc.id} data-testid={`handoff-row-${doc.doc_type}`} className="flex items-center gap-3 border border-white/10 p-3">
                    <FileText size={15} className="text-[#EAE04A] shrink-0" />
                    <div className="min-w-0 flex-1"><div className="font-mono-jb text-sm text-white truncate">{doc.title}</div></div>
                    <Button data-testid={`handoff-view-${doc.doc_type}`} onClick={() => openDoc(doc)} variant="outline" className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#00F0FF] h-8 text-[11px] font-mono-jb uppercase"><Eye size={13} className="mr-1" />View</Button>
                    <Button data-testid={`handoff-pdf-${doc.doc_type}`} onClick={() => download(doc, "pdf")} variant="outline" className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#FF3B30] h-8 text-[11px] font-mono-jb uppercase"><Download size={13} className="mr-1" />PDF</Button>
                  </div>
                ))}
              </div>
            </section>
          )}
          <section className="border border-white/10 bg-[#0B0C10] p-6 relative overflow-hidden">
            <div className="flex items-center gap-2 mb-5">
              <Cpu className="text-[#00F0FF]" size={18} />
              <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Generate Documentation</h2>
            </div>

            {/* Reviewer assignment (presales) */}
            {isPresales && (
              <div className="flex items-center gap-2 mb-4" data-testid="reviewer-row">
                <ClipboardCheck size={15} className="text-[#00F0FF] shrink-0" />
                <span className="font-mono-jb text-[11px] text-[#8B949E] shrink-0">Approver:</span>
                <Select value={project.reviewer_id || ""} onValueChange={setReviewer} disabled={isLocked}>
                  <SelectTrigger data-testid="reviewer-select" className="h-9 bg-[#0B0C10] border-white/15 font-mono-jb text-xs focus:ring-[#00F0FF] max-w-[280px]">
                    <SelectValue placeholder="Assign a reviewer" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                    {basicUsers.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`reviewer-option-${u.id}`}>{u.name || u.email} — {u.role}</SelectItem>)}
                  </SelectContent>
                </Select>
                {project.reviewer_name && <span className="text-[10px] text-[#484F58] font-mono-jb">Signs off before Final</span>}
              </div>
            )}

            {/* Client logo for the pack cover */}
            <div className="flex items-center gap-3 mb-4 border border-white/10 p-3" data-testid="logo-control">
              <ImageIcon size={16} className="text-[#00F0FF] shrink-0" />
              {project.client_logo ? (
                <img src={project.client_logo} alt="client logo" data-testid="client-logo-preview" className="h-8 max-w-[120px] object-contain bg-white/90 rounded-sm px-1" />
              ) : (
                <span className="font-mono-jb text-[11px] text-[#8B949E]">Add the client's logo to the pack cover</span>
              )}
              <div className="ml-auto flex items-center gap-2">
                <label data-testid="upload-logo-label" className="cursor-pointer flex items-center gap-1.5 border border-white/15 hover:border-[#00F0FF]/50 rounded-sm px-2.5 py-1.5 font-mono-jb text-[10px] uppercase tracking-wider text-white transition-colors duration-200">
                  {logoBusy ? <Loader2 className="animate-spin" size={13} /> : <UploadCloud size={13} />} {project.client_logo ? "Replace" : "Upload"}
                  <input data-testid="upload-logo-input" type="file" accept="image/*" className="hidden" onChange={uploadLogo} disabled={logoBusy} />
                </label>
                {project.client_logo && (
                  <button data-testid="remove-logo-button" onClick={removeLogo} className="text-[#484F58] hover:text-[#FF3B30] transition-colors duration-200"><Trash2 size={15} /></button>
                )}
              </div>
            </div>

            {/* Reusable templates (presales) */}
            {isPresales && (
              <div className="flex items-center gap-2 mb-4" data-testid="templates-row">
                <LayoutTemplate size={15} className="text-[#EAE04A] shrink-0" />
                <Select value={applyTpl} onValueChange={applyTemplate} disabled={templates.length === 0}>
                  <SelectTrigger data-testid="template-select" className="h-9 bg-[#0B0C10] border-white/15 font-mono-jb text-xs focus:ring-[#EAE04A] max-w-[280px]">
                    <SelectValue placeholder={templates.length ? "Apply a saved template" : "No templates yet"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                    {templates.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="save-template-button" onClick={saveTemplate} variant="outline"
                  className="h-9 rounded-sm border-[#EAE04A]/40 bg-transparent text-[#EAE04A] hover:bg-[#EAE04A]/10 hover:text-[#EAE04A] font-mono-jb text-[10px] uppercase tracking-wider shrink-0">
                  <Bookmark size={13} className="mr-1" /> Save as Template
                </Button>
              </div>
            )}

            <div className="mb-3 text-[11px] text-[#8B949E] font-mono-jb">Tick the items you want to generate & download:</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {genOrder.map((dt) => {
                const isCustom = customSections.some((c) => c.key === dt);
                return (
                <button key={dt} data-testid={`toggle-${dt}`} onClick={() => setSelected((s) => ({ ...s, [dt]: !s[dt] }))}
                  className={`relative text-left border p-4 transition-colors duration-200 ${selected[dt] ? "border-[#00F0FF]/50 bg-[#00F0FF]/5" : "border-white/10 hover:border-white/25"}`}>
                  {isCustom && (
                    <span data-testid={`remove-section-${dt}`} role="button" tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); removeSection(dt); }}
                      className="absolute top-1.5 right-1.5 text-[#484F58] hover:text-[#FF3B30] transition-colors duration-200"><X size={13} /></span>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="font-mono-jb font-bold text-base tracking-tighter" style={{ color: selected[dt] ? "#00F0FF" : "#fff" }}>{metaFor(dt).label}</span>
                    <span className={`w-4 h-4 border flex items-center justify-center ${selected[dt] ? "bg-[#00F0FF] border-[#00F0FF]" : "border-white/30"}`}>
                      {selected[dt] && <span className="text-black text-[10px] font-bold">✓</span>}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#8B949E] mt-1 truncate">{metaFor(dt).full}</div>
                </button>
                );
              })}
            </div>
            {isPresales && (
              <div className="flex items-center gap-2 mb-5" data-testid="add-section-row">
                <Input data-testid="new-section-input" value={newSection} onChange={(e) => setNewSection(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addSection(); }}
                  placeholder="Add a custom section (e.g. Migration Plan, Risk Register)"
                  className="bg-[#0B0C10] border-white/15 font-mono-jb text-sm h-10 focus-visible:ring-[#EAE04A]" />
                <Button data-testid="add-section-button" onClick={addSection} disabled={!newSection.trim()}
                  className="rounded-sm bg-[#EAE04A] text-black font-mono-jb uppercase text-[11px] tracking-wider h-10 px-3 font-bold hover:bg-[#EAE04A] shrink-0">
                  <Plus size={15} className="mr-1" /> Add
                </Button>
              </div>
            )}
            <Button data-testid="generate-button" onClick={generate} disabled={generating || isLocked}
              className="w-full h-12 rounded-sm bg-[#00FF41] text-black font-mono-jb uppercase text-xs tracking-[0.2em] font-bold hover:bg-[#00FF41] hover:-translate-y-0.5 transition-transform duration-200 disabled:opacity-40 disabled:hover:translate-y-0">
              {generating ? <><Loader2 className="animate-spin mr-2" size={18} /> AI is working...</> : <><Sparkles size={16} className="mr-2" /> Generate with AI</>}
            </Button>
            {isPresales && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3" data-testid="workflow-actions">
                <Button data-testid="revise-button" onClick={reviseAll} disabled={revising || isLocked || openComments === 0} variant="outline"
                  title={openComments === 0 ? "Add a review comment on a document first, then this applies your comments" : "Rewrite documents that have open comments"}
                  className="rounded-sm h-10 font-mono-jb uppercase text-[10px] tracking-wider border-[#00F0FF]/40 bg-transparent text-[#00F0FF] hover:bg-[#00F0FF]/10 hover:text-[#00F0FF] disabled:opacity-50">
                  {revising ? <Loader2 className="animate-spin" size={14} /> : <><RotateCw size={13} className="mr-1" /> Regenerate w/ Comments{openComments ? ` (${openComments})` : ""}</>}
                </Button>
                <Button data-testid="save-version-button" onClick={() => setVerOpen(true)} disabled={isLocked} variant="outline"
                  className="rounded-sm h-10 font-mono-jb uppercase text-[10px] tracking-wider border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#EAE04A]">
                  <ClipboardCheck size={13} className="mr-1" /> Save Version
                </Button>
                <Button data-testid="select-download-button" onClick={() => { setDlSel({}); setDlOpen(true); }} variant="outline"
                  className="rounded-sm h-10 font-mono-jb uppercase text-[10px] tracking-wider border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#00FF41]">
                  <Download size={13} className="mr-1" /> Select & Download
                </Button>
              </div>
            )}
            {isPresales && (
              <div className="mt-3 text-[10px] text-[#484F58] font-mono-jb leading-relaxed" data-testid="workflow-hint">
                Flow: Generate → add a comment &amp; Regenerate to refine → Reviewer approves each document → Mark as WON (unlocks once every document is approved).
              </div>
            )}
            {generating && (
              <div className="mt-4 font-mono-jb text-xs text-[#00FF41] scanlines p-3 border border-[#00FF41]/20" data-testid="generating-indicator">
                {isPresales ? (
                  <>&gt; reading opportunity brief...<br />&gt; drafting {genProgress || "documents"}...<br />&gt; composing pre-sales pack<span className="cursor-blink">_</span></>
                ) : (
                  <>&gt; parsing device configurations...<br />&gt; generating {genProgress || "documents"}...<br />&gt; composing design documents<span className="cursor-blink">_</span></>
                )}
              </div>
            )}
          </section>

          {docsByType["topology"] && (
            <section className="border border-white/10 bg-[#0B0C10] p-6" data-testid="topology-section">
              <div className="flex items-center gap-2 mb-4">
                <Network className="text-[#00F0FF]" size={18} />
                <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Network Topology</h2>
              </div>
              <div className="border border-white/10 bg-white p-2 rounded-sm">
                <img data-testid="topology-diagram"
                  src={`${API}/projects/${id}/diagram.png?ts=${encodeURIComponent(docsByType["topology"].created_at || "")}`}
                  alt="Network topology diagram" className="w-full object-contain" />
              </div>
              <div className="text-[10px] text-[#8B949E] font-mono-jb mt-2">
                Auto-generated topology · embedded into every downloaded document. Re-run "Diagram" to refresh.
              </div>
            </section>
          )}

          <section className="border border-white/10 bg-[#0B0C10] p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <FileText className="text-[#EAE04A]" size={18} />
                <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Generated Documents</h2>
              </div>
              {docOrder.filter((dt) => docsByType[dt]).length > 0 && (
                <Button data-testid="download-all-button" onClick={downloadAll} disabled={downloadingAll}
                  className="rounded-sm bg-[#00FF41] text-black hover:bg-[#00FF41] font-mono-jb text-[11px] uppercase tracking-wider h-9 font-bold">
                  {downloadingAll ? <Loader2 className="animate-spin" size={14} /> : <><FileArchive size={14} className="mr-1" /> Download All (ZIP)</>}
                </Button>
              )}
            </div>
            {isPresales && approvableDocs.length > 0 && (
              <div className="mb-5" data-testid="approval-progress">
                <div className="flex justify-between items-center text-[10px] font-mono-jb uppercase tracking-wider mb-1.5">
                  <span className="text-[#8B949E]">Reviewer Approvals</span>
                  <span className={approvedCount === approvableDocs.length ? "text-[#00FF41]" : "text-[#8B949E]"} data-testid="approval-count">{approvedCount} of {approvableDocs.length} approved</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-sm overflow-hidden">
                  <div className="h-full bg-[#00FF41] transition-all duration-300" style={{ width: `${approvableDocs.length ? (approvedCount / approvableDocs.length) * 100 : 0}%` }} />
                </div>
                {approvedCount < approvableDocs.length && (
                  <div className="text-[10px] text-[#484F58] font-mono-jb mt-1.5">All documents must be approved by the reviewer before you can Mark as WON.</div>
                )}
              </div>
            )}
            {docOrder.filter((dt) => docsByType[dt]).length === 0 ? (
              <div className="text-[#484F58] text-sm font-mono-jb py-6 text-center border border-dashed border-white/10">
                <FileCode2 className="mx-auto mb-2" size={28} />
                No documents generated yet.
              </div>
            ) : (
              <div className="space-y-3" data-testid="documents-list">
                {docOrder.filter((dt) => docsByType[dt]).map((dt) => {
                  const doc = docsByType[dt];
                  const editor = doc.updated_by_initials || doc.created_by_initials;
                  const when = fmtWhen(doc.updated_at || doc.created_at);
                  return (
                    <div key={doc.id} data-testid={`document-row-${dt}`}
                      className="flex items-center gap-4 border border-white/10 p-4 hover:border-[#EAE04A]/40 transition-colors duration-200">
                      <div className="font-mono-jb font-extrabold text-2xl tracking-tighter text-[#EAE04A] w-24 shrink-0">{metaFor(dt)?.label || dt}</div>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono-jb text-sm text-white">{doc.title}</div>
                        <div className="text-[10px] text-[#8B949E] uppercase tracking-wider mt-0.5 flex items-center gap-2 flex-wrap">
                          <span>via {doc.provider === "azure" ? "Azure OpenAI (Private)" : "AI Engine"}</span>
                          {when && (
                            <span className="text-[#484F58] normal-case tracking-normal" data-testid={`doc-meta-${dt}`}>
                              · {doc.updated_by_initials ? "edited" : "created"} {editor ? `by ${editor} ` : ""}{when}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isPresales && NON_APPROVAL_DOC_TYPES.includes(dt) && (
                          <span data-testid={`no-approval-badge-${dt}`} className="font-mono-jb text-[10px] uppercase tracking-wider text-[#8B949E] border border-white/15 bg-white/5 rounded-sm px-2 h-9 flex items-center">Auto · no approval</span>
                        )}
                        {isPresales && !NON_APPROVAL_DOC_TYPES.includes(dt) && !isLocked && (
                          <button data-testid={`toggle-required-${dt}`} onClick={() => setRequired(doc.id, doc.sow_required === false)}
                            title={doc.sow_required === false ? "Optional — click to require for SOW" : "Required for SOW — click to make optional"}
                            className={`font-mono-jb text-[9px] uppercase tracking-wider rounded-sm px-2 h-9 border flex items-center gap-1 transition-colors duration-150 ${doc.sow_required === false ? "text-[#8B949E] border-white/15 bg-white/5 hover:text-white" : "text-[#00F0FF] border-[#00F0FF]/30 bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20"}`}>
                            {doc.sow_required === false ? "Optional" : "Required"}
                          </button>
                        )}
                        {isPresales && !NON_APPROVAL_DOC_TYPES.includes(dt) && doc.sow_required === false && (
                          <span data-testid={`optional-badge-${dt}`} className="font-mono-jb text-[10px] uppercase tracking-wider text-[#8B949E] border border-white/15 bg-white/5 rounded-sm px-2 h-9 flex items-center">Not gating</span>
                        )}
                        {isPresales && !NON_APPROVAL_DOC_TYPES.includes(dt) && doc.sow_required !== false && (
                          doc.approved ? (
                            <span data-testid={`approved-badge-${dt}`} title={`Approved by ${doc.approved_by_name || doc.approved_by_initials || ""}`}
                              className="flex items-center gap-1 font-mono-jb text-[10px] uppercase tracking-wider text-[#00FF41] border border-[#00FF41]/30 bg-[#00FF41]/10 rounded-sm px-2 h-9">
                              <CheckCircle2 size={13} /> Approved
                              {canApprove && !isLocked && <X size={12} className="ml-1 cursor-pointer hover:text-[#FF3B30]" data-testid={`revoke-approve-${dt}`} onClick={() => approveDoc(doc.id, false)} />}
                            </span>
                          ) : canApprove && !isLocked ? (
                            <Button data-testid={`approve-doc-${dt}`} onClick={() => approveDoc(doc.id, true)}
                              className="rounded-sm bg-[#00FF41] text-black hover:bg-[#00FF41] font-mono-jb text-[11px] uppercase tracking-wider h-9">
                              <CheckCircle2 size={14} className="mr-1" /> Approve
                            </Button>
                          ) : (
                            <span data-testid={`pending-badge-${dt}`} className="font-mono-jb text-[10px] uppercase tracking-wider text-[#EAE04A] border border-[#EAE04A]/30 bg-[#EAE04A]/10 rounded-sm px-2 h-9 flex items-center">Pending review</span>
                          )
                        )}
                        <Button data-testid={`view-doc-${dt}`} onClick={() => openDoc(doc)} variant="outline"
                          className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#00F0FF] font-mono-jb text-[11px] uppercase tracking-wider h-9">
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                        <Button data-testid={`download-pdf-${dt}`} onClick={() => download(doc, "pdf")}
                          className="rounded-sm bg-[#FF3B30]/90 text-white hover:bg-[#FF3B30] font-mono-jb text-[11px] uppercase tracking-wider h-9">
                          <Download size={14} className="mr-1" /> PDF
                        </Button>
                        <Button data-testid={`download-docx-${dt}`} onClick={() => download(doc, "docx")}
                          className="rounded-sm bg-[#00F0FF] text-black hover:bg-[#00F0FF] font-mono-jb text-[11px] uppercase tracking-wider h-9">
                          <Download size={14} className="mr-1" /> Word
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {isPresales && (
            <section className="border border-white/10 bg-[#0B0C10] p-6" data-testid="versions-section">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardCheck className="text-[#EAE04A]" size={18} />
                <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Version History</h2>
              </div>
              {versions.length >= 2 && (
                <div className="flex flex-wrap items-center gap-2 mb-4 border border-white/10 p-3" data-testid="version-diff-row">
                  <span className="font-mono-jb text-[10px] uppercase tracking-wider text-[#8B949E]">Compare</span>
                  <Select value={diffA} onValueChange={setDiffA}>
                    <SelectTrigger data-testid="diff-version-a" className="h-8 w-[140px] bg-[#0B0C10] border-white/15 font-mono-jb text-[11px]"><SelectValue placeholder="Version A" /></SelectTrigger>
                    <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                      {versions.map((v) => <SelectItem key={v.id} value={v.id}>{v.version} {v.stage}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={diffB} onValueChange={setDiffB}>
                    <SelectTrigger data-testid="diff-version-b" className="h-8 w-[140px] bg-[#0B0C10] border-white/15 font-mono-jb text-[11px]"><SelectValue placeholder="Version B" /></SelectTrigger>
                    <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                      {versions.map((v) => <SelectItem key={v.id} value={v.id}>{v.version} {v.stage}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={diffDoc} onValueChange={setDiffDoc}>
                    <SelectTrigger data-testid="diff-doc-select" className="h-8 w-[150px] bg-[#0B0C10] border-white/15 font-mono-jb text-[11px]"><SelectValue placeholder="Document" /></SelectTrigger>
                    <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                      {versionDocTypes.map((dt) => <SelectItem key={dt} value={dt}>{metaFor(dt)?.full || dt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button data-testid="run-version-diff" onClick={() => { setDiffData(null); setDiffOpen(true); runVersionDiff(); }} disabled={!diffA || !diffB || diffA === diffB || !diffDoc}
                    className="h-8 rounded-sm bg-[#00F0FF] text-black font-mono-jb text-[10px] uppercase tracking-wider hover:bg-[#00F0FF] px-3">Diff</Button>
                </div>
              )}
              {versions.length === 0 ? (
                <div className="text-[#484F58] text-sm font-mono-jb py-4">No versions saved yet. Use "Save Version" to snapshot the pack (e.g. v1.0 Draft).</div>
              ) : (
                <div className="space-y-2" data-testid="versions-list">
                  {versions.map((v) => (
                    <div key={v.id} data-testid={`version-row-${v.id}`} className="flex items-center gap-3 border border-white/10 px-3 py-2.5">
                      <span className={`font-mono-jb text-xs font-bold px-2 py-1 rounded-sm shrink-0 ${v.baseline ? "text-[#00FF41] bg-[#00FF41]/10" : "text-[#EAE04A] bg-[#EAE04A]/10"}`}>{v.version}</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono-jb text-[12px] text-white truncate">{v.stage}{v.baseline ? " · BASELINE" : ""}{v.note ? ` — ${v.note}` : ""}</div>
                        <div className="text-[10px] text-[#8B949E] font-mono-jb truncate">{v.user_name || v.user_email} · {fmtWhen(v.at)}</div>
                      </div>
                      {!isLocked && (
                        <button data-testid={`restore-version-${v.id}`} onClick={() => restoreVersion(v.id, `${v.version} ${v.stage}`)}
                          title="Restore this version" className="text-[#484F58] hover:text-[#00F0FF] transition-colors duration-200"><RotateCw size={15} /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          <section className="border border-white/10 bg-[#0B0C10] p-6" data-testid="activity-section">
            <div className="flex items-center gap-2 mb-4">
              <History className="text-[#00F0FF]" size={18} />
              <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Activity — who did what</h2>
            </div>
            {activity.length === 0 ? (
              <div className="text-[#484F58] text-sm font-mono-jb py-4">No activity recorded yet.</div>
            ) : (
              <div className="space-y-2" data-testid="activity-list">
                {activity.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 border border-white/10 px-3 py-2.5">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-sm bg-[#00F0FF]/15 text-[#00F0FF] text-[9px] font-bold shrink-0 font-mono-jb">
                      {a.user_initials || "?"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-jb text-[12px] text-white truncate">{a.detail}</div>
                      <div className="text-[10px] text-[#8B949E] font-mono-jb truncate">{a.user_name || a.user_email}</div>
                    </div>
                    <div className="text-[10px] text-[#484F58] font-mono-jb shrink-0">{fmtWhen(a.at)}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Doc viewer */}
      <Dialog open={!!viewDoc} onOpenChange={(o) => !o && setViewDoc(null)}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00F0FF] flex items-center justify-between pr-8 gap-2">
              <span className="truncate">{viewDoc?.title}</span>
              {viewDoc && (
                <span className="flex gap-2 shrink-0">
                  {editing ? (
                    <>
                      <Button data-testid="viewer-save-edit" onClick={saveDoc} disabled={savingDoc} className="rounded-sm bg-[#00FF41] hover:bg-[#00FF41] text-black h-8 text-[11px] font-mono-jb uppercase">
                        {savingDoc ? <Loader2 className="animate-spin" size={13} /> : <><Save size={13} className="mr-1" />Save</>}
                      </Button>
                      <Button data-testid="viewer-cancel-edit" onClick={() => setEditing(false)} variant="outline" className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 h-8 text-[11px] font-mono-jb uppercase"><X size={13} className="mr-1" />Cancel</Button>
                    </>
                  ) : (
                    !isLocked && <Button data-testid="viewer-edit" onClick={() => { setEditContent(viewContent); setEditing(true); }} variant="outline" className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#00F0FF] h-8 text-[11px] font-mono-jb uppercase"><Pencil size={13} className="mr-1" />Edit</Button>
                  )}
                  <Button data-testid="viewer-download-pdf" onClick={() => download(viewDoc, "pdf")} className="rounded-sm bg-[#FF3B30]/90 hover:bg-[#FF3B30] text-white h-8 text-[11px] font-mono-jb uppercase"><Download size={13} className="mr-1" />PDF</Button>
                  <Button data-testid="viewer-download-docx" onClick={() => download(viewDoc, "docx")} className="rounded-sm bg-[#00F0FF] hover:bg-[#00F0FF] text-black h-8 text-[11px] font-mono-jb uppercase"><Download size={13} className="mr-1" />Word</Button>
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto pr-2" data-testid="doc-viewer-content">
            {loadingDoc ? (
              <div className="font-mono-jb text-[#8B949E] py-10 text-center"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading document...</div>
            ) : editing ? (
              <Textarea data-testid="doc-edit-textarea" value={editContent} onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[55vh] bg-[#0a0d14] border-white/15 font-mono-jb text-[13px] leading-relaxed text-[#d8dee9] focus-visible:ring-[#00F0FF]" />
            ) : (
              <div className="md-doc"><Markdown remarkPlugins={[remarkGfm]}>{viewContent}</Markdown></div>
            )}
          </div>
          {viewDoc && isPresales && !editing && (
            <div className="border-t border-white/10 pt-4 mt-2" data-testid="viewer-comments">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquarePlus size={15} className="text-[#EAE04A]" />
                <span className="font-mono-jb text-[11px] uppercase tracking-[0.15em] text-white">Review Comments</span>
              </div>
              <div className="space-y-2 max-h-[22vh] overflow-y-auto mb-3">
                {comments.filter((c) => c.doc_type === viewDoc.doc_type).length === 0 ? (
                  <div className="text-[11px] text-[#484F58] font-mono-jb">No comments yet. Add change requests below, then Regenerate to apply them.</div>
                ) : comments.filter((c) => c.doc_type === viewDoc.doc_type).map((c) => (
                  <div key={c.id} data-testid={`comment-${c.id}`} className={`flex items-start gap-2 border px-3 py-2 ${c.resolved ? "border-white/5 opacity-50" : "border-white/15"}`}>
                    <button data-testid={`resolve-comment-${c.id}`} onClick={() => toggleComment(c.id)} className={`mt-0.5 shrink-0 ${c.resolved ? "text-[#00FF41]" : "text-[#484F58] hover:text-[#00FF41]"}`}><CheckCircle2 size={14} /></button>
                    <div className="min-w-0 flex-1">
                      <div className={`font-mono-jb text-[12px] ${c.resolved ? "line-through text-[#8B949E]" : "text-white"}`}>{c.text}</div>
                      <div className="text-[10px] text-[#484F58] font-mono-jb">{c.user_initials || c.user_name} · {fmtWhen(c.created_at)}</div>
                    </div>
                    <button data-testid={`delete-comment-${c.id}`} onClick={() => deleteComment(c.id)} className="text-[#484F58] hover:text-[#FF3B30] shrink-0"><X size={13} /></button>
                  </div>
                ))}
              </div>
              {!isLocked && (
                <div className="flex items-center gap-2">
                  <Input data-testid="new-comment-input" value={newComment} onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addComment(viewDoc.doc_type); }}
                    placeholder="Request a change (e.g. add a hypercare phase)"
                    className="bg-[#0a0d14] border-white/15 font-mono-jb text-xs h-9 focus-visible:ring-[#EAE04A]" />
                  <Button data-testid="add-comment-button" onClick={() => addComment(viewDoc.doc_type)} disabled={!newComment.trim()}
                    className="rounded-sm bg-[#EAE04A] text-black font-mono-jb text-[10px] uppercase h-9 px-3 hover:bg-[#EAE04A] shrink-0"><Send size={13} /></Button>
                  <label data-testid="feedback-upload-label" className="cursor-pointer flex items-center gap-1 border border-white/15 hover:border-[#00F0FF]/50 rounded-sm px-2.5 h-9 font-mono-jb text-[10px] uppercase text-white shrink-0 transition-colors duration-200">
                    {fbBusy ? <Loader2 className="animate-spin" size={13} /> : <UploadCloud size={13} />} Feedback
                    <input data-testid="feedback-upload-input" type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={(e) => uploadFeedback(e, viewDoc.doc_type)} disabled={fbBusy} />
                  </label>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00F0FF] flex items-center gap-2">
              <Share2 size={16} /> Share Project
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-5">
            <div className="flex items-center justify-between border border-white/10 p-4">
              <div>
                <div className="font-mono-jb text-sm text-white">Read-only link</div>
                <div className="text-[11px] text-[#8B949E] mt-0.5">Colleagues can view & download docs — they cannot edit or upload.</div>
              </div>
              <Switch data-testid="share-toggle" checked={shareEnabled} disabled={shareBusy} onCheckedChange={toggleShare} />
            </div>

            {shareEnabled && (
              <div className="fade-up">
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb flex items-center gap-1.5">
                  <Link2 size={11} /> Shareable URL
                </label>
                <div className="mt-2 flex gap-2">
                  <Input data-testid="share-link-input" readOnly value={shareUrl}
                    className="bg-[#0B0C10] border-white/15 font-mono-jb text-xs focus-visible:ring-[#00F0FF]" />
                  <Button data-testid="copy-link-button" onClick={copyLink}
                    className="rounded-sm bg-[#00F0FF] text-black hover:bg-[#00F0FF] font-mono-jb text-[11px] uppercase shrink-0 h-10">
                    {copied ? <Check size={15} /> : <Copy size={15} />}
                  </Button>
                </div>
                <a data-testid="open-share-link" href={shareUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-[11px] font-mono-jb uppercase tracking-wider text-[#00FF41] hover:underline">
                  <Eye size={12} /> Open read-only view
                </a>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert to Delivery dialog */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono-jb uppercase tracking-wider text-[#EAE04A] flex items-center gap-2">
              <ArrowRightLeft size={16} /> Convert to Delivery
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#8B949E] leading-relaxed">
              This creates a new <span className="text-white">Delivery</span> project for <span className="text-[#EAE04A]">{project.name}</span>,
              carrying over the client and pre-sales brief. Enter the PS Code from the config generator to link it.
            </p>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">PS Code</label>
              <Input data-testid="convert-pscode-input" value={convertPs} onChange={(e) => setConvertPs(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") convert(); }}
                placeholder="Unique PS code from the config generator" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#EAE04A]" />
            </div>
            <Button data-testid="convert-submit" onClick={convert} disabled={converting}
              className="w-full rounded-sm bg-[#EAE04A] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#EAE04A]">
              {converting ? <Loader2 className="animate-spin" size={16} /> : "Create Delivery Project"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Version dialog */}
      <Dialog open={verOpen} onOpenChange={setVerOpen}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white" data-testid="version-dialog">
          <DialogHeader><DialogTitle className="font-mono-jb uppercase tracking-wider text-[#EAE04A] flex items-center gap-2"><ClipboardCheck size={16} /> Save Version</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#8B949E] leading-relaxed">Snapshot the current pack so you can track its evolution (e.g. v1.0 Draft → v1.1 Client Feedback → v2.0 Final Signed).</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Version</label>
                <Input data-testid="version-number-input" value={verForm.version} onChange={(e) => setVerForm({ ...verForm, version: e.target.value })} placeholder="v1.0" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#EAE04A]" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Stage</label>
                <Select value={verForm.stage} onValueChange={(v) => setVerForm({ ...verForm, stage: v })}>
                  <SelectTrigger data-testid="version-stage-select" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus:ring-[#EAE04A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                    <SelectItem value="Draft">Draft</SelectItem>
                    <SelectItem value="Client Feedback">Client Feedback</SelectItem>
                    <SelectItem value="Final Signed">Final Signed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Note (optional)</label>
              <Input data-testid="version-note-input" value={verForm.note} onChange={(e) => setVerForm({ ...verForm, note: e.target.value })} placeholder="What changed in this version" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#EAE04A]" />
            </div>
            <Button data-testid="version-save-submit" onClick={saveVersion} disabled={savingVer} className="w-full rounded-sm bg-[#EAE04A] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#EAE04A]">
              {savingVer ? <Loader2 className="animate-spin" size={16} /> : "Save Version"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* WON double-confirm dialog */}
      <Dialog open={wonOpen} onOpenChange={(o) => { setWonOpen(o); if (!o) setWonStep(1); }}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white" data-testid="won-dialog">
          <DialogHeader><DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00FF41] flex items-center gap-2"><Trophy size={16} /> Mark Opportunity WON</DialogTitle></DialogHeader>
          {wonStep === 1 ? (
            <div className="space-y-4 py-2">
              <p className="text-sm text-[#8B949E] leading-relaxed">This locks the current pack as the <span className="text-[#00FF41]">Baseline SOW</span> (read-only) and marks the opportunity WON. Set the baseline version details:</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Version</label>
                  <Input data-testid="won-version-input" value={wonForm.version} onChange={(e) => setWonForm({ ...wonForm, version: e.target.value })} placeholder="v2.0" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00FF41]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Stage</label>
                  <Input data-testid="won-stage-input" value={wonForm.stage} onChange={(e) => setWonForm({ ...wonForm, stage: e.target.value })} placeholder="Final Signed" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00FF41]" />
                </div>
              </div>
              <Button data-testid="won-continue" onClick={() => setWonStep(2)} className="w-full rounded-sm bg-[#00FF41] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00FF41]">Continue</Button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="border border-[#00FF41]/30 bg-[#00FF41]/5 p-4">
                <p className="text-sm text-white font-mono-jb">Confirm: lock <span className="text-[#00FF41]">{wonForm.version} — {wonForm.stage}</span> as the signed Baseline SOW?</p>
                <p className="text-[11px] text-[#8B949E] mt-2">All documents become read-only. Only an admin can unlock later. This cannot be undone casually.</p>
              </div>
              <div className="flex gap-2">
                <Button data-testid="won-back" onClick={() => setWonStep(1)} variant="outline" className="flex-1 rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 font-mono-jb uppercase text-xs">Back</Button>
                <Button data-testid="won-confirm" onClick={doMarkWon} disabled={wonBusy} className="flex-1 rounded-sm bg-[#00FF41] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00FF41]">
                  {wonBusy ? <Loader2 className="animate-spin" size={16} /> : "Yes, Lock as WON"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Select & Download dialog */}
      <Dialog open={dlOpen} onOpenChange={setDlOpen}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white" data-testid="download-select-dialog">
          <DialogHeader><DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00FF41] flex items-center gap-2"><Download size={16} /> Select Documents</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <p className="text-sm text-[#8B949E]">Tick the final documents to export (ZIP with PDF + Word). These can feed your Config Generator.</p>
            {docOrder.filter((dt) => docsByType[dt]).length === 0 ? (
              <div className="text-[11px] text-[#484F58] font-mono-jb py-4">No documents generated yet.</div>
            ) : docOrder.filter((dt) => docsByType[dt]).map((dt) => (
              <label key={dt} data-testid={`dl-check-${dt}`} className="flex items-center gap-3 border border-white/10 px-3 py-2.5 cursor-pointer hover:border-white/25">
                <input type="checkbox" checked={!!dlSel[dt]} onChange={(e) => setDlSel({ ...dlSel, [dt]: e.target.checked })} className="accent-[#00FF41]" />
                <span className="font-mono-jb text-sm text-white">{docsByType[dt].title}</span>
              </label>
            ))}
            <Button data-testid="download-selected-submit" onClick={downloadSelected} className="w-full mt-2 rounded-sm bg-[#00FF41] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00FF41]">Download ZIP</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version Diff dialog */}
      <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white max-w-5xl" data-testid="version-diff-dialog">
          <DialogHeader><DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00F0FF]">Version Diff</DialogTitle></DialogHeader>
          {!diffData ? (
            <div className="font-mono-jb text-[#8B949E] py-10 text-center"><Loader2 className="animate-spin inline mr-2" size={16} /> Computing diff...</div>
          ) : (
            <>
              <div className="flex items-center gap-5 mb-3 font-mono-jb text-xs">
                <span className="text-[#FF9A93]">A: {diffData.aLabel}</span>
                <span className="text-[#7CFFA0]">B: {diffData.bLabel}</span>
                <span className="text-[#7CFFA0]">+{diffData.adds}</span>
                <span className="text-[#FF9A93]">-{diffData.dels}</span>
                {diffData.adds === 0 && diffData.dels === 0 && <span className="text-[#8B949E]">No differences</span>}
              </div>
              <div className="flex gap-3">
                {[["a", diffData.left, "#FF3B30"], ["b", diffData.right, "#00FF41"]].map(([side, lines, col]) => (
                  <pre key={side} data-testid={`diff-pane-${side}`} className="flex-1 min-w-0 overflow-auto max-h-[60vh] text-[11px] leading-[1.6] font-mono-jb m-0 border border-white/10 bg-[#0a0d14]">
                    {lines.map((ln, i) => (
                      <div key={i} className={ln.t === "del" ? "bg-[#FF3B30]/12 text-[#FF9A93] px-3" : ln.t === "add" ? "bg-[#00FF41]/12 text-[#7CFFA0] px-3" : "text-[#8B949E] px-3"}>
                        <span className="select-none mr-2 opacity-50">{ln.t === "del" ? "-" : ln.t === "add" ? "+" : " "}</span>{ln.l || "\u00A0"}
                      </div>
                    ))}
                  </pre>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ChangeOrdersPanel projectId={id} ccOpportunityId={project?.cc_opportunity_id} />
    </div>
  );
}
